import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoIcon from "@mui/icons-material/Info";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "../../styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import { textStyle } from "../../styles/Style";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveOperationHoursTooltip,
} from "../../../constants/ReserveTooltipInformation";
import {
  converttoDayjsTime,
  converttoStandardTime,
} from "../../../constants/PropertyInformation";
import { WEEKDAY_LABELS_MON_SUN } from "../../../constants/Days";
import useScrollToTop from "../../hooks/useScrollToTop";

const DAY_KEY = "Day";
const OPEN_START_KEY = "Open Start";
const OPEN_END_KEY = "Open End";
const SHIFTS_KEY = "Shifts";
const CODE_KEY = "Code";
const NAME_KEY = "Name";

type StaffShiftForm = {
  start: dayjs.Dayjs | null;
  end: dayjs.Dayjs | null;
  label: string;
};

const ReserveOperationHours = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [day, setDay] = useState<string>(WEEKDAY_LABELS_MON_SUN[0]);
  const [openStart, setOpenStart] = useState<dayjs.Dayjs | null>(() =>
    dayjs(`${new Date().toISOString().split("T")[0]}T06:00:00`),
  );
  const [openEnd, setOpenEnd] = useState<dayjs.Dayjs | null>(() =>
    dayjs(`${new Date().toISOString().split("T")[0]}T21:00:00`),
  );
  const [staffShifts, setStaffShifts] = useState<StaffShiftForm[]>([]);
  const [editClicked, setEditClicked] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [operationHoursInformation, setOperationHoursInformation] = useState<
    any[]
  >([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const openRangeLabel =
    openStart && openEnd && openStart.isValid() && openEnd.isValid()
      ? `${openStart.format("h:mm A")} - ${openEnd.format("h:mm A")}`
      : "";
  const staffShiftTextParts = staffShifts
    .map((s) => {
      const a = s.start && s.start.isValid() ? s.start.format("h:mm A") : "";
      const b = s.end && s.end.isValid() ? s.end.format("h:mm A") : "";
      if (s.label.trim()) {
        if (a && b) return `${a} - ${b} (${s.label.trim()})`;
        return s.label.trim();
      }
      if (a && b) return `${a} - ${b}`;
      return "";
    })
    .filter(Boolean);
  const listName = [openRangeLabel, staffShiftTextParts.join(", ")]
    .filter(Boolean)
    .join(" | ");
  const staffShiftsJson = JSON.stringify(
    staffShifts
      .filter((s) => s.label.trim() || (s.start?.isValid() && s.end?.isValid()))
      .map((s) => ({
        start: s.start?.isValid() ? converttoStandardTime(s.start!) : null,
        end: s.end?.isValid() ? converttoStandardTime(s.end!) : null,
        label: s.label.trim(),
      })),
  );

  const payload = {
    [DAY_KEY]: day.trim(),
    [OPEN_START_KEY]:
      openStart && openStart.isValid() ? converttoStandardTime(openStart) : "",
    [OPEN_END_KEY]:
      openEnd && openEnd.isValid() ? converttoStandardTime(openEnd) : "",
    [SHIFTS_KEY]: staffShiftsJson,
    [CODE_KEY]: day.trim(),
    [NAME_KEY]: listName,
  };

  const validateInput = (): boolean => {
    if (!day || !String(day).trim()) {
      setAlertMessage("Day is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!openStart || !openEnd || !openStart.isValid() || !openEnd.isValid()) {
      setAlertMessage("Open hours start and end are required");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!openStart.isBefore(openEnd)) {
      setAlertMessage("Open hours end must be after start");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    for (let i = 0; i < staffShifts.length; i++) {
      const s = staffShifts[i];
      const hasLabel = Boolean(s.label.trim());
      const hasStart = s.start && s.start.isValid();
      const hasEnd = s.end && s.end.isValid();
      if (!hasLabel && !hasStart && !hasEnd) {
        continue;
      }
      if (!hasLabel) {
        if (hasStart !== hasEnd) {
          setAlertMessage(
            `Staff shift ${i + 1}: provide both start and end, or only a shift name.`,
          );
          setAlertType("error");
          setOpenAlert(true);
          return false;
        }
        if (hasStart && hasEnd && !s.start!.isBefore(s.end!)) {
          setAlertMessage(`Staff shift ${i + 1}: end must be after start.`);
          setAlertType("error");
          setOpenAlert(true);
          return false;
        }
      }
    }
    return true;
  };

  const editRow = (list: any) => {
    list?.id && setSelectedId(String(list.id));
    list !== null && setEditClicked(true);
    if (!list) {
      setDay(WEEKDAY_LABELS_MON_SUN[0]);
      setOpenStart(dayjs(`${new Date().toISOString().split("T")[0]}T06:00:00`));
      setOpenEnd(dayjs(`${new Date().toISOString().split("T")[0]}T21:00:00`));
      setStaffShifts([]);
      return;
    }
    setDay(
      list[DAY_KEY] != null && String(list[DAY_KEY]).length > 0
        ? String(list[DAY_KEY])
        : String(list[CODE_KEY] ?? WEEKDAY_LABELS_MON_SUN[0]),
    );
    setOpenStart(
      list[OPEN_START_KEY]
        ? converttoDayjsTime(list[OPEN_START_KEY])
        : dayjs(`${new Date().toISOString().split("T")[0]}T06:00:00`),
    );
    setOpenEnd(
      list[OPEN_END_KEY]
        ? converttoDayjsTime(list[OPEN_END_KEY])
        : dayjs(`${new Date().toISOString().split("T")[0]}T21:00:00`),
    );
    const raw = list[SHIFTS_KEY];
    let nextShifts: StaffShiftForm[] = [];
    if (typeof raw === "string" && raw.trim()) {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          nextShifts = arr.map((x: any) => ({
            start: x?.start ? converttoDayjsTime(x.start) : null,
            end: x?.end ? converttoDayjsTime(x.end) : null,
            label: typeof x?.label === "string" ? x.label : "",
          }));
        }
      } catch {
        nextShifts = [];
      }
    }
    setStaffShifts(nextShifts.length ? nextShifts : []);
  };

  const deleteRow = (id: number) => {
    setOperationHoursInformation(
      deleteItem(operationHoursInformation ?? [], id),
    );
  };

  const addShift = () => {
    setStaffShifts((prev) => [...prev, { start: null, end: null, label: "" }]);
  };

  const removeShift = (index: number) => {
    setStaffShifts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateShift = (index: number, patch: Partial<StaffShiftForm>) => {
    setStaffShifts((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const updateRow = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (operationHoursInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DAY_KEY,
        String(day).trim(),
      )
    ) {
      setAlertMessage("Cannot have duplicate days");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setOperationHoursInformation((prev: any) =>
      (prev ?? []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editRow(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.operationHours} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRow = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        operationHoursInformation ?? [],
        DAY_KEY,
        String(day).trim(),
      )
    ) {
      setAlertMessage("Cannot have duplicate days");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setOperationHoursInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRow(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.operationHours} added successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  useEffect(() => {
    setOperationHoursInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(operationHoursInformation);
    editClicked &&
      setSelectedId(
        checkData(operationHoursInformation, selectedId) ? selectedId : null,
      );
  }, [operationHoursInformation]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="column" gap="10px">
        <div ref={topRef} />
        <Stack direction="column" width="100%">
          <Paper
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              padding: "20px",
            }}
          >
            <Stack
              direction="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
              mb={4}
            >
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="div" sx={textStyle}>
                  {ReservePropertyEnum.operationHours}
                  <Tooltip
                    title={tooltipComponent(
                      reserveOperationHoursTooltip.header,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Typography>
                <Stack
                  direction="row"
                  gap="10px"
                  justifyContent="space-between"
                >
                  {editClicked && (
                    <ClearButton
                      click={() => {
                        editRow(null);
                        setSelectedId(null);
                        setEditClicked(false);
                      }}
                    />
                  )}
                  {lastUpdatedRecord && (
                    <CopyButton
                      click={() => {
                        editRow({
                          ...lastUpdatedRecord,
                          id: undefined,
                        });
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>

            <Stack
              className="form-checkbox-row form-two-col-row"
              direction="row"
              width="100%"
              gap="20px"
              flexWrap="wrap"
            >
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Days
                  <Tooltip
                    title={tooltipComponent(reserveOperationHoursTooltip.day)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="reserve-op-hours-day">Day</InputLabel>
                  <Select
                    labelId="reserve-op-hours-day"
                    label="Day"
                    value={day}
                    onChange={(e) => setDay(String(e.target.value))}
                  >
                    {WEEKDAY_LABELS_MON_SUN.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Open hours
                  <Tooltip
                    title={tooltipComponent(
                      reserveOperationHoursTooltip.openHours,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  gap={2}
                  flexWrap="wrap"
                  width="100%"
                >
                  <TimePicker
                    label="Opens"
                    value={openStart}
                    onChange={(v) => setOpenStart(v)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: { minWidth: 160 },
                      },
                    }}
                  />
                  <TimePicker
                    label="Closes"
                    value={openEnd}
                    onChange={(v) => setOpenEnd(v)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: { minWidth: 160 },
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack spacing={1.5} width="100%">
              <Stack
                className="form-two-col-row"
                direction="row"
                alignItems="center"
                gap={0.75}
              >
                <FormLabel component="legend" sx={{ m: 0 }}>
                  Shifts
                </FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    reserveOperationHoursTooltip.staffShifts,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Add one row per window. Use start and end times, or only a shift
                name (for example Late Night). Preview: {listName || "—"}
              </Typography>
              {staffShifts.map((shift, idx) => (
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  key={idx}
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <TimePicker
                    label="Shift start"
                    value={shift.start}
                    onChange={(v) => updateShift(idx, { start: v })}
                    slotProps={{
                      textField: { size: "small", sx: { minWidth: 160 } },
                    }}
                  />
                  <TimePicker
                    label="Shift end"
                    value={shift.end}
                    onChange={(v) => updateShift(idx, { end: v })}
                    slotProps={{
                      textField: { size: "small", sx: { minWidth: 160 } },
                    }}
                  />
                  <TextField
                    label="Shift Name"
                    placeholder="Late Night"
                    size="small"
                    value={shift.label}
                    onChange={(e) =>
                      updateShift(idx, { label: e.target.value })
                    }
                    sx={{ minWidth: 180, flex: 1 }}
                  />
                  <IconButton
                    aria-label="Remove shift"
                    onClick={() => removeShift(idx)}
                    color="error"
                    size="small"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={addShift}
                sx={{ alignSelf: "flex-start" }}
              >
                Add shift
              </Button>
            </Stack>

            <Stack direction="row" width="100%" justifyContent="flex-end">
              <Button
                variant="contained"
                sx={{
                  cursor: "pointer",
                  border: "1px solid gray",
                  borderRadius: "10px",
                  background: "darkgreen",
                  color: "white",
                }}
                onClick={() => (selectedId ? updateRow() : addRow())}
              >
                {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
                {ReservePropertyEnum.operationHours}
              </Button>
            </Stack>

            <Stack direction="column" gap="10px" mt={4}>
              <Typography
                variant="body2"
                sx={{ color: "red", fontWeight: 800 }}
              >
                Do not use sample data. Those are for demo purposes only!
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {ReservePropertyEnum.operationHours} available
                <Tooltip
                  title={tooltipComponent(reserveListAvailableHeadingTooltip)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Stack direction="column" gap="10px">
                {Array.isArray(operationHoursInformation) &&
                operationHoursInformation.length > 0 ? (
                  <DraggableItemList
                    itemData={operationHoursInformation}
                    onOrderUpdate={(order) =>
                      setOperationHoursInformation(order)
                    }
                    onEdit={(info) => {
                      editRow(info);
                      scrollToTop();
                    }}
                    onDelete={(id) => deleteRow(id)}
                    code="Code"
                    name="Name"
                  />
                ) : (
                  <Typography variant="body2">
                    No {ReservePropertyEnum.operationHours} information
                    available
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default ReserveOperationHours;
